import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { Area, AreaChart, CartesianGrid, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import { fetchPriceVarianceGrouped, fetchPurchasesMonthly, type PriceVarianceFilters, type VarianceGroupRow } from "../../api/reports";
import DataRecovery from "../../components/DataRecovery";
import Spinner from "../../components/Spinner";
import { filterColor } from "../../lib/filterColors";
import { formatDateShort, formatEuro, toNumber } from "../../lib/format";
import ShareDonutCard, { type ShareRow } from "./ShareDonutCard";
import {
  buildSeasons,
  currentMonth,
  lastDayOfMonth,
  MONTH_INITIALS,
  monthLabel,
  monthLongLabel,
  purchaseFilters,
  regularity,
  seasonYears,
  strongMonths,
  usePurchasesSummary,
  type ManufacturerSeason,
} from "./purchases";

const MONTH_NAMES = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];
const EMERALD = "#28785e";

function compactEuro(value: number): string {
  return value >= 1000 ? `${Math.round(value / 1000).toLocaleString("de-AT")} T€` : formatEuro(value);
}

function spendRows(rows: readonly VarianceGroupRow[] | undefined, axis: "supplier" | "manufacturer"): ShareRow[] {
  return (rows ?? []).flatMap((row) => {
    const id = axis === "supplier" ? row.supplier : row.manufacturer_id;
    if (!id) return [];
    return [{ id, label: axis === "supplier" ? id : row.manufacturer_name ?? id, value: Math.max(0, toNumber(row.total_spend) ?? 0) }];
  });
}

/**
 * Reiter „Einkaufsvolumen“ (Beta-Feedback 2026-10-07, Entwurf C3): was wann
 * eingekauft wurde, bei wem und von welchem Hersteller – und ob Hersteller
 * jedes Jahr zur selben Zeit gekauft werden.
 */
export default function PurchasesTab({
  filters,
  onFilter,
}: {
  filters: PriceVarianceFilters;
  onFilter: (key: "supplier" | "manufacturer_id", id: string) => void;
}) {
  const { query, summary } = usePurchasesSummary(filters);
  const scope = purchaseFilters(filters);
  // The rings cover the same complete months as the tiles and the table.
  const lastMonth = summary?.months[summary.months.length - 1]?.month;
  const monthEnd = lastMonth ? lastDayOfMonth(lastMonth) : undefined;
  const completeTo = monthEnd && scope.to && scope.to < monthEnd ? scope.to : monthEnd;
  const supplierQuery = useQuery({
    queryKey: ["reports", "purchases-by-supplier", scope.from, completeTo, scope.manufacturer_id],
    queryFn: () => fetchPriceVarianceGrouped({ from: scope.from, to: completeTo, manufacturer_id: scope.manufacturer_id, group_by: "supplier", sort: "spend_desc" }),
    enabled: completeTo !== undefined,
    staleTime: 60_000,
  });
  const manufacturerQuery = useQuery({
    queryKey: ["reports", "purchases-by-manufacturer", scope.from, completeTo, scope.supplier],
    queryFn: () => fetchPriceVarianceGrouped({ from: scope.from, to: completeTo, supplier: scope.supplier, group_by: "manufacturer", sort: "spend_desc" }),
    enabled: completeTo !== undefined,
    staleTime: 60_000,
  });

  if (query.isError) {
    return <DataRecovery error={query.error} subject="Die Ausgaben" onRetry={() => void query.refetch()} />;
  }
  if (!summary) {
    return <div className="flex h-48 items-center justify-center"><Spinner size="sm" label="Lade Einkaufsvolumen …" /></div>;
  }
  if (summary.months.length === 0) {
    return <p className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600 shadow-sm">In diesem Zeitraum gibt es keine abgeschlossenen Monate mit Einkäufen.</p>;
  }
  const { months, highest, lowest } = summary;
  const range = `${monthLongLabel(months[0].month)} – ${monthLongLabel(months[months.length - 1].month)}`;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="purchases-tiles">
        <Tile label="Einkaufsvolumen" value={formatEuro(summary.total)} note={range} />
        <Tile label="Pro Monat im Schnitt" value={formatEuro(summary.average)} note={`${Math.round(summary.lines / months.length).toLocaleString("de-AT")} Positionen`} />
        <Tile label="Stärkster Monat" value={highest ? formatEuro(highest.spend) : "–"} note={highest ? monthLongLabel(highest.month) : ""} />
        <Tile label="Schwächster Monat" value={lowest ? formatEuro(lowest.spend) : "–"} note={lowest ? monthLongLabel(lowest.month) : ""} />
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
        <ShareDonutCard
          title="Nach Großhändler"
          rows={spendRows(supplierQuery.data?.items, "supplier")}
          colorFor={(id) => filterColor("supplier", id)}
          isLoading={supplierQuery.isPending}
          isError={supplierQuery.isError}
          emptyText="Keine Einkäufe in dieser Auswahl."
          selected={filters.supplier}
          onSelect={(id) => onFilter("supplier", id)}
          restLabel={(count) => `Übrige ${count}`}
          showBars={false}
          size={112}
          stacked
        />
        <section aria-label="Verlauf" className="min-w-0 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <h2 className="text-sm font-semibold text-slate-900">Verlauf</h2>
          <p className="mt-0.5 text-xs text-slate-600">netto, nach Belegdatum</p>
          <div className="mt-3 h-56">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={months.map((row) => ({ label: monthLabel(row.month), spend: row.spend }))} margin={{ top: 16, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                <YAxis tickFormatter={(value: number) => compactEuro(value)} tick={{ fontSize: 11, fill: "#64748b" }} tickLine={false} axisLine={false} width={56} />
                <Tooltip formatter={(value) => [formatEuro(Number(value)), "Ausgaben"]} />
                <ReferenceLine y={summary.average} stroke="#94a3b8" strokeDasharray="4 4" label={{ value: `Schnitt ${compactEuro(summary.average)}`, position: "insideTopRight", fill: "#64748b", fontSize: 11 }} />
                <Area type="linear" dataKey="spend" stroke={EMERALD} strokeWidth={2} fill={EMERALD} fillOpacity={0.1} dot={{ r: 3, fill: "#fff", stroke: EMERALD, strokeWidth: 2 }} isAnimationActive={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </section>
      </div>

      <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
        <ShareDonutCard
          title="Nach Hersteller"
          rows={spendRows(manufacturerQuery.data?.items, "manufacturer")}
          colorFor={(id) => filterColor("manufacturer", id)}
          isLoading={manufacturerQuery.isPending}
          isError={manufacturerQuery.isError}
          emptyText="Keine Einkäufe in dieser Auswahl."
          selected={filters.manufacturer_id}
          onSelect={(id) => onFilter("manufacturer_id", id)}
          restLabel={(count) => `Übrige (${count})`}
          showBars={false}
          size={132}
          topN={8}
          stacked
        />
        <MonthTable summary={summary} />
      </div>

      <Seasons filters={filters} />
    </div>
  );
}

function Tile({ label, value, note }: { label: string; value: string; note: string }) {
  return (
    <div className="min-w-0 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
      <div className="text-xs text-slate-600">{label}</div>
      <div className="mt-0.5 truncate text-xl font-semibold tabular-nums tracking-tight text-slate-900">{value}</div>
      <div className="mt-0.5 truncate text-xs text-slate-600">{note}</div>
    </div>
  );
}

function MonthTable({ summary }: { summary: NonNullable<ReturnType<typeof usePurchasesSummary>["summary"]> }) {
  const rows = [...summary.months].reverse();
  const max = Math.max(...summary.months.map((row) => row.spend), 0);
  const share = (overpay: number, spend: number) => (spend > 0 ? `${((overpay / spend) * 100).toLocaleString("de-AT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %` : "–");
  return (
    <section aria-label="Monat für Monat" className="min-w-0 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-sm font-semibold text-slate-900">Monat für Monat</h2>
        <span className="text-xs text-slate-600">neuester Monat oben</span>
      </div>
      <div className="mt-2 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-[11px] uppercase tracking-wide text-slate-500">
              <th className="py-2 pr-3 text-left font-medium">Monat</th>
              <th className="px-3 py-2 text-right font-medium">Ausgaben</th>
              <th className="hidden w-32 px-3 py-2 md:table-cell" aria-hidden="true" />
              <th className="hidden px-3 py-2 text-right font-medium sm:table-cell">Positionen</th>
              <th className="hidden px-3 py-2 text-right font-medium sm:table-cell">Belege</th>
              <th className="hidden px-3 py-2 text-right font-medium lg:table-cell">Artikel</th>
              <th className="px-3 py-2 text-right font-medium">Mehrkosten</th>
              <th className="hidden py-2 pl-3 text-right font-medium sm:table-cell">Anteil</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.month} data-month-row className="border-t border-slate-100">
                <td className="py-2 pr-3 whitespace-nowrap text-slate-800">{monthLabel(row.month)}</td>
                <td className="px-3 py-2 text-right font-semibold tabular-nums text-slate-900">{formatEuro(row.spend)}</td>
                <td className="hidden px-3 py-2 md:table-cell" aria-hidden="true">
                  <span className="block h-2 overflow-hidden rounded-full bg-slate-100">
                    <span className="block h-full rounded-full" style={{ width: `${max > 0 ? (row.spend / max) * 100 : 0}%`, backgroundColor: EMERALD }} />
                  </span>
                </td>
                <td className="hidden px-3 py-2 text-right tabular-nums text-slate-700 sm:table-cell">{row.lines.toLocaleString("de-AT")}</td>
                <td className="hidden px-3 py-2 text-right tabular-nums text-slate-700 sm:table-cell">{row.documents.toLocaleString("de-AT")}</td>
                <td className="hidden px-3 py-2 text-right tabular-nums text-slate-700 lg:table-cell">{row.articles.toLocaleString("de-AT")}</td>
                <td className="px-3 py-2 text-right tabular-nums text-slate-700">{formatEuro(row.overpay)}</td>
                <td className="hidden py-2 pl-3 text-right tabular-nums text-slate-600 sm:table-cell">{share(row.overpay, row.spend)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-slate-200 font-semibold">
              <td className="py-2 pr-3">{summary.months.length} Monate</td>
              <td className="px-3 py-2 text-right tabular-nums">{formatEuro(summary.total)}</td>
              <td className="hidden md:table-cell" />
              <td className="hidden px-3 py-2 text-right tabular-nums sm:table-cell">{summary.lines.toLocaleString("de-AT")}</td>
              <td className="hidden px-3 py-2 text-right tabular-nums sm:table-cell">{summary.documents.toLocaleString("de-AT")}</td>
              <td className="hidden lg:table-cell" />
              <td className="px-3 py-2 text-right tabular-nums">{formatEuro(summary.overpay)}</td>
              <td className="hidden py-2 pl-3 text-right tabular-nums text-slate-600 sm:table-cell">{share(summary.overpay, summary.total)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      {summary.running ? (
        <p className="mt-2 text-xs text-slate-600">
          {monthLongLabel(summary.running.month)} ist noch nicht vollständig ({formatEuro(summary.running.spend)}
          {summary.dataUntil ? ` bis zum Beleg vom ${formatDateShort(summary.dataUntil)}` : ""}) und zählt erst, wenn er abgeschlossen ist.
        </p>
      ) : summary.dataUntil && summary.dataUntil.slice(0, 7) < currentMonth() ? (
        <p className="mt-2 text-xs text-slate-600">Belege liegen bis {formatDateShort(summary.dataUntil)} vor.</p>
      ) : null}
    </section>
  );
}

/**
 * „Wann kaufen wir welchen Hersteller?“ – Anteil der Jahresausgaben je
 * Kalendermonat über die letzten drei vollen Jahre, unabhängig vom Zeitraum
 * oben (der wäre für ein Saisonmuster zu kurz). Großhändler und Hersteller
 * filtern mit.
 */
function Seasons({ filters }: { filters: PriceVarianceFilters }) {
  const years = seasonYears();
  const query = useQuery({
    queryKey: ["reports", "purchases-seasons", years.from, filters.supplier, filters.manufacturer_id],
    queryFn: () => fetchPurchasesMonthly({ from: years.from, to: years.to, supplier: filters.supplier, manufacturer_id: filters.manufacturer_id, group_by: "manufacturer" }),
    staleTime: 300_000,
  });
  const seasons = useMemo(() => buildSeasons(query.data?.items ?? []), [query.data]);
  return (
    <section aria-label="Wann kaufen wir welchen Hersteller?" className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <h2 className="text-sm font-semibold text-slate-900">Wann kaufen wir welchen Hersteller?</h2>
      <p className="mt-0.5 text-xs text-slate-600">
        Anteil der Jahresausgaben je Kalendermonat, {years.label} zusammen · farbig = mindestens 1,5 × so viel wie im Schnitt · gestrichelt = gleichmäßig (8 %)
      </p>
      {query.isError ? (
        <div className="mt-3"><DataRecovery error={query.error} subject="Die Saisonmuster" onRetry={() => void query.refetch()} /></div>
      ) : query.isPending ? (
        <div className="flex h-32 items-center justify-center"><Spinner size="sm" label="Lade Saisonmuster …" /></div>
      ) : seasons.length === 0 ? (
        <p className="mt-3 text-sm text-slate-600">Für {years.label} gibt es keine Einkäufe in dieser Auswahl.</p>
      ) : (
        <ul className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5" data-testid="purchases-seasons">
          {seasons.map((season) => <SeasonCard key={season.id} season={season} />)}
        </ul>
      )}
    </section>
  );
}

function SeasonCard({ season }: { season: ManufacturerSeason }) {
  const color = filterColor("manufacturer", season.id);
  const strong = strongMonths(season.shares);
  const top = strong[0];
  const max = Math.max(20, ...season.shares);
  const even = 100 / 12;
  const regular = top !== undefined ? regularity(season, top) : null;
  return (
    <li className="rounded-lg border border-slate-100 p-3" data-season-card>
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
        <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: color }} />
        <span className="truncate">{season.name}</span>
      </div>
      <div className="relative mt-2 h-14" aria-hidden="true">
        <div className="absolute inset-x-0 border-t border-dashed border-slate-400" style={{ bottom: `${(even / max) * 100}%` }} />
        <div className="flex h-full items-end gap-[3px]">
          {season.shares.map((share, index) => (
            <span
              key={index}
              title={`${MONTH_NAMES[index]}: ${share.toLocaleString("de-AT", { maximumFractionDigits: 0 })} %`}
              className="flex-1 rounded-sm"
              style={{ height: `${(share / max) * 100}%`, backgroundColor: strong.includes(index) ? color : "#cbd5e1" }}
            />
          ))}
        </div>
      </div>
      <div className="mt-1 flex gap-[3px] text-[9px] text-slate-400" aria-hidden="true">
        {MONTH_INITIALS.map((initial, index) => <span key={index} className="flex-1 text-center">{initial}</span>)}
      </div>
      <p className="mt-2 text-xs leading-snug text-slate-700">
        {top === undefined ? "übers Jahr verteilt" : (
          <>
            Schwerpunkt {[...strong].sort((a, b) => a - b).map((index) => MONTH_NAMES[index]).join(", ")}
            <br />
            {/* Regularity needs at least two years with purchases. */}
            {regular && regular.years >= 2
              ? `${MONTH_NAMES[top]} in ${regular.hits} von ${regular.years} Jahren stark${regular.hits === regular.years ? " – jedes Jahr" : ""}`
              : "nur ein Jahr mit Einkäufen"}
          </>
        )}
      </p>
    </li>
  );
}
